import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  AudioLines,
  CheckCircle2,
  Loader2,
  MicVocal,
  Plus,
  Trash2,
  Upload,
  WandSparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  CLONE_LANGUAGE_OPTIONS,
  aliyunVoiceCloneService,
  formatDuration,
  type CloneLanguage,
  type ClonedVoice,
} from '@/features/voice-clone/services/aliyun-voice-clone-service'

/** 格式化文件大小 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function VoiceCloneCanvas() {
  // --- 创建克隆音色状态 ---
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [prefix, setPrefix] = useState('myvoice')
  const [language, setLanguage] = useState<CloneLanguage>('zh')
  const [isCreating, setIsCreating] = useState(false)
  const [createProgress, setCreateProgress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [createdVoiceId, setCreatedVoiceId] = useState<string | null>(null)

  // --- 音色列表状态 ---
  const [voices, setVoices] = useState<ClonedVoice[]>([])
  const [loadingList, setLoadingList] = useState(true)
  const [deletingVoiceId, setDeletingVoiceId] = useState<string | null>(null)

  // --- 试听状态 ---
  const [testVoiceId, setTestVoiceId] = useState<string | null>(null)
  const [testText, setTestText] = useState('大家好，这是一段用克隆音色生成的语音试听。')
  const [isGenerating, setIsGenerating] = useState(false)
  const [testResult, setTestResult] = useState<{
    objectUrl: string
    duration: number
    voiceId: string
  } | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  // 加载音色列表
  const refreshList = useCallback(async () => {
    setLoadingList(true)
    setError(null)
    try {
      const result = await aliyunVoiceCloneService.listVoices()
      setVoices(result.voices)
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载音色列表失败')
    } finally {
      setLoadingList(false)
    }
  }, [])

  useEffect(() => {
    void refreshList()
  }, [refreshList])

  // 清理 objectUrl
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl)
      if (testResult) URL.revokeObjectURL(testResult.objectUrl)
    }
  }, [audioUrl, testResult])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('audio/')) {
      setError('请上传音频文件（wav / mp3 / m4a）。')
      return
    }

    if (audioUrl) URL.revokeObjectURL(audioUrl)

    setAudioFile(file)
    setAudioUrl(URL.createObjectURL(file))
    setError(null)
    setCreatedVoiceId(null)
    event.target.value = ''
  }

  const handleCreate = async () => {
    if (!audioFile || !prefix.trim()) return

    setIsCreating(true)
    setError(null)
    setCreateProgress('正在上传音频并复刻音色...')
    setCreatedVoiceId(null)

    try {
      const result = await aliyunVoiceCloneService.createVoice({
        audioFile,
        prefix: prefix.trim(),
        languageHints: language,
      })
      setCreatedVoiceId(result.voiceId)
      setCreateProgress(null)
      await refreshList()
      setTestVoiceId(result.voiceId)
    } catch (err) {
      setError(err instanceof Error ? err.message : '声音复刻失败。')
      setCreateProgress(null)
    } finally {
      setIsCreating(false)
    }
  }

  const handleDelete = async (voiceId: string) => {
    if (!window.confirm(`确定删除音色「${voiceId}」吗？此操作不可撤销。`)) return

    setDeletingVoiceId(voiceId)
    setError(null)
    try {
      await aliyunVoiceCloneService.deleteVoice(voiceId)
      setVoices((prev) => prev.filter((v) => v.voiceId !== voiceId))
      if (testVoiceId === voiceId) {
        setTestVoiceId(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除音色失败')
    } finally {
      setDeletingVoiceId(null)
    }
  }

  const handleGenerate = async () => {
    if (!testVoiceId || !testText.trim()) return

    setIsGenerating(true)
    setError(null)

    try {
      if (testResult) URL.revokeObjectURL(testResult.objectUrl)

      const result = await aliyunVoiceCloneService.generateSpeech({
        voiceId: testVoiceId,
        text: testText,
      })

      setTestResult({
        objectUrl: result.objectUrl,
        duration: result.duration,
        voiceId: testVoiceId,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : '试听生成失败。')
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center gap-4">
        <Link
          to="/app"
          className="inline-flex size-9 items-center justify-center rounded-lg border border-border bg-card text-foreground transition-colors hover:bg-accent"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">语音克隆</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            上传参考音频，复刻专属音色，用任意文案生成配音。
          </p>
        </div>
      </div>

      <div className="grid gap-8 xl:grid-cols-[1.15fr_0.85fr]">
        {/* Left: 创建克隆音色 */}
        <section className="space-y-6">
          {/* 上传参考音频 */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">参考音频</Label>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="audio/wav,audio/mpeg,audio/mp4,.wav,.mp3,.m4a"
              onChange={handleFileChange}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-card px-6 py-8 text-center transition-colors hover:border-primary/40 hover:bg-accent"
            >
              {audioUrl ? (
                <>
                  <AudioLines className="size-7 text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    {audioFile?.name ?? '已选择音频'}
                  </span>
                  {audioFile ? (
                    <span className="text-xs text-muted-foreground">
                      {formatFileSize(audioFile.size)}
                    </span>
                  ) : null}
                  <audio controls src={audioUrl} className="mt-2 w-full max-w-sm" />
                  <span className="mt-1 text-xs text-muted-foreground">点击重新选择</span>
                </>
              ) : (
                <>
                  <Upload className="size-7 text-muted-foreground/50" />
                  <span className="text-sm font-medium text-foreground">点击上传参考音频</span>
                  <span className="text-xs text-muted-foreground">
                    建议 10 秒以上清晰人声，支持 wav / mp3 / m4a
                  </span>
                </>
              )}
            </button>
          </div>

          {/* 音色名称 + 语种 */}
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-3">
              <Label htmlFor="voice-prefix" className="text-sm font-medium">
                音色前缀
              </Label>
              <Input
                id="voice-prefix"
                value={prefix}
                onChange={(event) => setPrefix(event.target.value)}
                placeholder="仅字母数字，如 myvoice"
                maxLength={10}
              />
              <p className="text-xs text-muted-foreground">用于生成音色 ID 标识。</p>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">音频语种</Label>
              <Select
                value={language}
                onValueChange={(value) => setLanguage(value as CloneLanguage)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择语种" />
                </SelectTrigger>
                <SelectContent>
                  {CLONE_LANGUAGE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">与音频实际语种保持一致效果更好。</p>
            </div>
          </div>

          {/* 创建按钮 */}
          <Button
            onClick={() => void handleCreate()}
            disabled={!audioFile || !prefix.trim() || isCreating}
            className="w-full sm:w-auto"
          >
            {isCreating ? <Loader2 className="size-4 animate-spin" /> : <WandSparkles className="size-4" />}
            复刻音色
          </Button>

          {createProgress ? (
            <div className="rounded-lg border border-primary/15 bg-primary/5 px-3.5 py-2.5 text-sm text-primary">
              {createProgress}
            </div>
          ) : null}

          {createdVoiceId ? (
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3.5 py-2.5 text-sm text-emerald-700">
              <div className="flex items-center gap-2 font-medium">
                <CheckCircle2 className="size-4" />
                音色复刻成功
              </div>
              <div className="mt-1 text-xs text-emerald-700/80">
                音色 ID：{createdVoiceId}，可在右侧试听或用于 AI 配音。
              </div>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3.5 py-2.5 text-sm text-destructive">
              {error}
            </div>
          ) : null}
        </section>

        {/* Right: 音色列表 + 试听 */}
        <section className="space-y-6">
          {/* 音色列表 */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">已克隆音色</Label>
              <button
                type="button"
                onClick={() => void refreshList()}
                className="text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                刷新
              </button>
            </div>

            {loadingList ? (
              <div className="flex items-center justify-center rounded-xl border border-border bg-card py-8 text-sm text-muted-foreground">
                <Loader2 className="mr-2 size-4 animate-spin" />
                加载中...
              </div>
            ) : voices.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-10 text-center">
                <MicVocal className="size-7 text-muted-foreground/50" />
                <p className="mt-3 text-sm text-muted-foreground">还没有克隆音色</p>
                <p className="mt-1 text-xs text-muted-foreground">在左侧上传音频开始复刻</p>
              </div>
            ) : (
              <div className="max-h-[280px] space-y-1 overflow-y-auto">
                {voices.map((item) => {
                  const selected = item.voiceId === testVoiceId
                  return (
                    <div
                      key={item.voiceId}
                      className={`flex items-center justify-between gap-3 rounded-lg border px-3.5 py-2.5 transition-colors ${
                        selected
                          ? 'border-primary/40 bg-primary/5'
                          : 'border-border bg-card hover:bg-accent'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setTestVoiceId(item.voiceId)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <div className="flex items-center gap-2">
                          <AudioLines className="size-3.5 shrink-0 text-muted-foreground" />
                          <span className="truncate text-sm font-medium text-foreground">
                            {item.voiceId}
                          </span>
                        </div>
                        <div className="mt-0.5 truncate text-xs text-muted-foreground">
                          {item.status === 'OK' ? '可用' : item.status || '未知'} · {item.gmtCreate || '未知时间'}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(item.voiceId)}
                        disabled={deletingVoiceId === item.voiceId}
                        className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                        title="删除"
                      >
                        {deletingVoiceId === item.voiceId ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="size-3.5" />
                        )}
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* 试听生成 */}
          {testVoiceId ? (
            <div className="space-y-4 rounded-xl border border-border bg-card p-5">
              <div className="space-y-3">
                <Label className="text-sm font-medium">试听文案</Label>
                <Textarea
                  value={testText}
                  onChange={(event) => setTestText(event.target.value)}
                  placeholder="输入要试听的文案"
                  className="min-h-[80px] resize-y"
                />
              </div>

              <Button
                onClick={() => void handleGenerate()}
                disabled={!testText.trim() || isGenerating}
                className="w-full"
              >
                {isGenerating ? <Loader2 className="size-4 animate-spin" /> : <MicVocal className="size-4" />}
                生成试听
              </Button>

              {testResult ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="truncate">{testResult.voiceId}</span>
                    <span>{formatDuration(testResult.duration)}</span>
                  </div>
                  <audio controls src={testResult.objectUrl} className="w-full" />
                </div>
              ) : null}
            </div>
          ) : (
            <div className="flex min-h-[120px] flex-col items-center justify-center rounded-xl border border-dashed border-border px-8 text-center">
              <Plus className="size-6 text-muted-foreground/50" />
              <p className="mt-2 text-xs text-muted-foreground">
                选择一个克隆音色后可在此试听
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
